import { useState, useEffect, useRef, useCallback } from "react";

// Timeline: pause → uphill → downhill → pause → downhill → uphill
const PAUSE = 1000;
const RAMP = 5000;
const CYCLE = PAUSE + RAMP + RAMP + PAUSE + RAMP + RAMP; // 21s

function computeTilt(elapsed: number): number {
  const t = elapsed % CYCLE;
  if (t < PAUSE) return 0;
  if (t < PAUSE + RAMP) return Math.sin(((t - PAUSE) / RAMP) * (Math.PI / 2));
  if (t < PAUSE + RAMP * 2) return Math.cos(((t - PAUSE - RAMP) / RAMP) * (Math.PI / 2));
  if (t < PAUSE * 2 + RAMP * 2) return 0;
  if (t < PAUSE * 2 + RAMP * 3)
    return -Math.sin(((t - PAUSE * 2 - RAMP * 2) / RAMP) * (Math.PI / 2));
  return -Math.cos(((t - PAUSE * 2 - RAMP * 3) / RAMP) * (Math.PI / 2));
}

const BLUE = [102, 199, 255] as const;
const RED = [239, 83, 80] as const;
const GREEN = [102, 187, 106] as const;

function lerpColor(base: readonly number[], target: readonly number[], t: number) {
  const r = Math.round(base[0] + (target[0] - base[0]) * t);
  const g = Math.round(base[1] + (target[1] - base[1]) * t);
  const b = Math.round(base[2] + (target[2] - base[2]) * t);
  return `rgb(${r},${g},${b})`;
}

function lighten(r: number, g: number, b: number, amount: number) {
  return `rgb(${Math.round(r + (255 - r) * amount)},${Math.round(g + (255 - g) * amount)},${Math.round(b + (255 - b) * amount)})`;
}

function formatTime(totalSeconds: number) {
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.round(totalSeconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function GAPGraph({
  activeStep,
  stepId,
  isMetric,
}: {
  activeStep: number;
  stepId: number;
  isMetric: boolean;
}) {
  const [tilt, setTilt] = useState(0);
  const startRef = useRef<number | null>(null);
  const frameRef = useRef<number>(0);

  const animate = useCallback((timestamp: number) => {
    if (startRef.current === null) startRef.current = timestamp;
    const elapsed = timestamp - startRef.current;
    const newTilt = computeTilt(elapsed);
    setTilt(newTilt);
    frameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (activeStep !== stepId) {
      startRef.current = null;
      setTilt(0);
      return;
    }

    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [activeStep, stepId, animate]);

  if (activeStep !== stepId) return null;

  // Bar tilt — symmetric pivot around center
  const leftTopY = 171 + tilt * 10;
  const rightTopY = 171 - tilt * 10;
  const barPath = `M183 ${leftTopY}L339 ${rightTopY}V191H183Z`;

  // GAP time: 6:00/km (metric) or 9:00/mile (imperial) base, ±30s
  const baseSeconds = isMetric ? 360 : 540;
  const gapSeconds = baseSeconds - tilt * 30;
  const gapValue = formatTime(gapSeconds);

  // Color: blue → red (uphill) or blue → green (downhill)
  const absTilt = Math.abs(tilt);
  const target = tilt > 0 ? RED : GREEN;
  const barColor = lerpColor(BLUE, target, absTilt);

  // Shimmer: lighter tint of current bar color
  const baseR = Math.round(BLUE[0] + (target[0] - BLUE[0]) * absTilt);
  const baseG = Math.round(BLUE[1] + (target[1] - BLUE[1]) * absTilt);
  const baseB = Math.round(BLUE[2] + (target[2] - BLUE[2]) * absTilt);
  const shimmerColor = lighten(baseR, baseG, baseB, 0.35);

  return (
    <svg
      width="361"
      height="274"
      viewBox="0 0 361 274"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Left Card — PACE */}
      <rect x="19" y="82" width="156" height="109" rx="8" fill="#F0F1F3" />
      <g clipPath="url(#gapLeftClip)">
        <rect x="19" y="171" width="156" height="20" fill="#66C7FF" />
      </g>
      <text
        x="97"
        y="106"
        textAnchor="middle"
        fill="black"
        fillOpacity="0.7"
        fontSize="14"
        fontFamily="Inter, sans-serif"
        fontWeight="500"
        letterSpacing="-0.087"
      >
        PACE
      </text>
      <text
        x="97"
        y="165"
        textAnchor="middle"
        fill="black"
        fontSize="50"
        fontFamily="Hubot Sans Condensed, sans-serif"
        fontWeight="700"
        letterSpacing="-0.175"
      >
        {isMetric ? "6:00" : "9:00"}
      </text>
      <text
        x="97"
        y="181"
        textAnchor="middle"
        fill="black"
        fillOpacity="0.7"
        fontSize="10"
        fontFamily="Inter, sans-serif"
        fontWeight="400"
      >
        {isMetric ? "min/km" : "min/mi"}
      </text>

      {/* Right Card — GAP */}
      <rect x="183" y="82" width="156" height="109" rx="8" fill="#F0F1F3" />
      <g clipPath="url(#gapRightClip)">
        <path d={barPath} fill={barColor} />
      </g>
      <text
        x="261"
        y="100"
        textAnchor="middle"
        fill="black"
        fillOpacity="0.7"
        fontSize="14"
        fontFamily="Inter, sans-serif"
        fontWeight="500"
        letterSpacing="-0.087"
      >
        GAP
      </text>
      <text
        x="261"
        y="116"
        textAnchor="middle"
        fill="black"
        fillOpacity="0.7"
        fontSize="10"
        fontFamily="Inter, sans-serif"
        fontWeight="400"
        letterSpacing="0.1"
      >
        Grade-adjusted pace
      </text>
      <text
        x="261"
        y="165"
        textAnchor="middle"
        fill="black"
        fontSize="50"
        fontFamily="Hubot Sans Condensed, sans-serif"
        fontWeight="700"
        letterSpacing="-0.175"
      >
        {gapValue}
      </text>
      <text
        x="261"
        y="181"
        textAnchor="middle"
        fill="black"
        fillOpacity="0.7"
        fontSize="10"
        fontFamily="Inter, sans-serif"
        fontWeight="400"
      >
        {isMetric ? "min/km" : "min/mi"}
      </text>

      <defs>
        <clipPath id="gapLeftClip">
          <rect x="19" y="82" width="156" height="109" rx="8" />
        </clipPath>
        <clipPath id="gapRightClip">
          <rect x="183" y="82" width="156" height="109" rx="8" />
        </clipPath>
        {/* Shimmer gradient — updates with bar color */}
        <linearGradient
          id="gapShimmerRight"
          x1="339"
          y1="168.5"
          x2="279"
          y2="168.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={barColor} />
          <stop offset="0.49" stopColor={shimmerColor} />
          <stop offset="1" stopColor={barColor} />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default GAPGraph;
